import { Injectable } from '@angular/core';

import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs';
import { StoreKeysMainEnum, StoreKeysSubEnum } from '../enums/store-keys.enum';
import { catchError, filter, map, startWith, switchMap, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class StorageService {
    public initialStore$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private changesKey$: Subject<string[]> = new Subject<string[]>();
    public storageChanges$ = this.changesKey$.asObservable();
    private storage: Storage | null = null;

    constructor(private storageService: Storage) {}

    public async create(): Promise<void> {
        this.storage = await this.storageService?.create();
        this.initialStore$.next(true);
    }

    public set<T>(key: string, value: T): Observable<T> {
        return this.initialStore$.pipe(
            filter(v => !!v),
            switchMap(() => from(this.storage!.set(key, value)).pipe(map(() => value))),
        );
    }

    public setToStore<T>(value: T, ...args: string[]): Observable<T> {
        if (args?.length > 1) {
            return this.get<T>(args[0]).pipe(
                switchMap(parenValue => {
                    const newObj = parenValue ? JSON.parse(JSON.stringify(parenValue)) : {};
                    const keys = args?.slice(1, args?.length);
                    keys.reduce((acc, key, index) => {
                        if (index === keys.length - 1) {
                            acc[key] = value;
                        } else {
                            if (!acc[key] || typeof acc[key] !== 'object') {
                                acc[key] = {};
                            }

                            return acc[key];
                        }

                        return acc;
                    }, newObj);

                    return this.set<T>(args[0], newObj).pipe(
                        tap(() => {
                            this.changesKey$.next(args);
                        }),
                    );
                }),
            );
        }

        if (args?.length) {
            return this.set<T>(args[0], value).pipe(
                tap(() => {
                    this.changesKey$.next(args);
                }),
            );
        }

        return of(value);
    }

    public getFromStore<T>(...args: string[]): Observable<T | null> {
        if (!args?.length) {
            return of(null);
        }

        return this.get<T>(args[0]).pipe(
            map(parenValue => {
                if (!parenValue) {
                    return null;
                }

                const keys = args.slice(1);

                return keys.reduce((acc, key) => {
                    // @ts-ignore
                    return acc && acc[key] !== undefined ? acc[key] : null;
                }, parenValue);
            }),
        );
    }

    public subscribeToChanges<T>(...args: string[]): Observable<T | null> {
        return this.storageChanges$.pipe(
            startWith(args),
            filter(values => values.every((item, index) => item === args[index])),
            switchMap(() => {
                return this.getFromStore<T>(...args);
            }),
        );
    }

    public setSubItem<V>(key: StoreKeysSubEnum, value: unknown, keyParent: StoreKeysMainEnum): Observable<V | null> {
        return this.get<V>(keyParent).pipe(
            catchError(() => {
                return of(null);
            }),
            switchMap(item => {
                if (item) {
                    return this.set<V>(keyParent, { ...item, [key]: value });
                }

                return this.set<V>(keyParent, { [key]: value } as V);
            }),
            tap(() => {
                this.changesKey$.next([keyParent, key]);
            }),
        );
    }

    public get<T>(key: string): Observable<T> {
        return this.initialStore$.pipe(
            filter(v => !!v),
            switchMap(() =>
                from(this.storage!.get(key)).pipe(
                    catchError(err => {
                        console.error('get store error', err);

                        return of(null);
                    }),
                ),
            ),
        );
    }

    public getSubItem<T>(key: StoreKeysMainEnum, subKey: StoreKeysSubEnum, log?: boolean): Observable<T> {
        if (log) {
            console.log('getSubItem ', subKey);
            console.log('this.storage ', { ...this.storage });
        }

        // @ts-ignore
        return this.get<T>(key).pipe(
            tap(v => {
                if (log) {
                    console.log('PRERERE TAP', v, subKey);
                }
            }),
            map(value => {
                if (log) {
                    console.log('getSubItem subKey map', value, subKey);
                }
                // @ts-ignore
                if (value && value[subKey]) {
                    if (log) {
                        // @ts-ignore
                        console.log('getSubItem subKey VALUE', subKey, value[subKey]);
                    }

                    // @ts-ignore
                    return value[subKey];
                } else {
                    return null;
                }
            }),
            tap(v => {
                if (log) {
                    console.log('SUBITEMRESULT', v, subKey);
                }
            }),
        );
    }

    public remove(key: string): Observable<void> {
        return this.storage ? from(this.storage?.remove(key)) : of();
    }

    public removeSubItem(key: StoreKeysSubEnum, keyParent: StoreKeysMainEnum): Observable<any> {
        return this.storage
            ? from(this.storage.get(keyParent)).pipe(
                  switchMap(item => {
                      if (item) {
                          delete item[key];

                          return from((this.storage as Storage).set(keyParent, item));
                      } else {
                          return of();
                      }
                  }),
              )
            : of();
    }

    public clear(): Observable<void> {
        return this.storage ? from(this.storage.clear()) : of();
    }
}
